/**
 * Phase 15.5 — Production Stability Dashboard
 *
 * Live view of the things that go wrong during an outage:
 *   - Process uptime (so we can spot recent restarts)
 *   - Mongo ping latency (so we can rule out DB-side stalls)
 *   - Background-task heartbeats (so a stalled drip loop is visible)
 *   - System metrics: memory, CPU, FDs
 *   - Last 50 unhandled errors (so the founder can pinpoint causes
 *     without SSH'ing the container)
 *
 * Polls every 10s. Admin-gated via the diagnostics endpoint.
 */
import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useRequireAdmin } from "@/lib/useRequireAdmin";

const fmtAge = (s) => {
  if (s == null) return "—";
  if (s < 60) return `${Math.round(s)}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${Math.round(s % 60)}s`;
  return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
};

const Pill = ({ tone = "ok", children }) => {
  const map = {
    ok:       "bg-emerald-500/15 border-emerald-400/40 text-emerald-200",
    warn:     "bg-amber-500/15 border-amber-400/40 text-amber-200",
    bad:      "bg-rose-500/15 border-rose-400/40 text-rose-200",
    info:     "bg-sky-500/10 border-sky-400/40 text-sky-200",
    neutral:  "bg-white/5 border-white/15 text-gray-200",
  }[tone] || "";
  return (
    <span className={`inline-flex items-center text-[10px] uppercase tracking-wider font-black px-2 py-0.5 rounded-full border ${map}`}>
      {children}
    </span>
  );
};

const StatCard = ({ label, value, tone = "neutral", hint }) => (
  <div className={`rounded-xl border px-4 py-3 ${
    tone === "ok"   ? "border-emerald-400/30 bg-emerald-500/5" :
    tone === "warn" ? "border-amber-400/30 bg-amber-500/5" :
    tone === "bad"  ? "border-rose-400/30 bg-rose-500/5" :
    "border-white/10 bg-white/[0.02]"
  }`}>
    <div className="text-[10px] uppercase tracking-wider font-bold text-gray-400">{label}</div>
    <div className="text-xl font-black text-white mt-0.5">{value}</div>
    {hint && <div className="text-[10px] text-gray-500 mt-0.5">{hint}</div>}
  </div>
);

const AdminRuntimeStatusPage = () => {
  const { authChecked } = useRequireAdmin();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [lastFetch, setLastFetch] = useState(null);
  const [loading, setLoading] = useState(false);
  // Track restarts: if the started_at flips between polls we surface a
  // banner so the founder sees "the container just restarted" instead
  // of having to do mental diff math.
  const [restartBanner, setRestartBanner] = useState(null);

  // Phase 15.6D — Cloudinary (media) health
  const [cloudHealth, setCloudHealth] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/status/diagnostics");
      setData((prev) => {
        if (prev?.uptime?.started_at && r.data?.uptime?.started_at
            && prev.uptime.started_at !== r.data.uptime.started_at) {
          setRestartBanner({
            old: prev.uptime.started_at,
            new: r.data.uptime.started_at,
            at: new Date().toISOString(),
          });
        }
        return r.data;
      });
      setLastFetch(new Date());
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load diagnostics");
    } finally {
      setLoading(false);
    }
    // Phase 15.6D — also poll Cloudinary health.
    try {
      const c = await api.get("/admin/media/health");
      setCloudHealth(c.data);
    } catch (_e) {
      // non-fatal — keep showing last good value
    }
  };

  useEffect(() => {
    if (!authChecked) return;
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [authChecked]);

  if (error && !data) {
    return (
      <div className="min-h-screen bg-[#0a0a0e] p-6 text-white">
        <h1 className="text-2xl font-black mb-3" data-testid="runtime-status-title">Runtime Status</h1>
        <Card className="p-4 border-rose-500/40 bg-rose-500/5">
          <div className="text-rose-200 font-bold">Failed to load diagnostics</div>
          <div className="text-rose-100/80 text-sm mt-1" data-testid="runtime-status-error">{error}</div>
          <Button
            onClick={load}
            className="mt-3 bg-amber-500 hover:bg-amber-400 text-black font-bold"
            data-testid="runtime-status-retry"
          >
            Retry
          </Button>
        </Card>
      </div>
    );
  }

  const dbOk = !!data?.db?.ok;
  const dbLat = data?.db?.latency_ms;
  const dbTone = !dbOk ? "bad" : (dbLat > 500 ? "warn" : "ok");
  const sys = data?.system || {};
  const memTone = sys.memory_rss_mb > 800 ? "bad"
                 : sys.memory_rss_mb > 500 ? "warn" : "ok";
  const cpuTone = sys.cpu_percent > 80 ? "bad"
                 : sys.cpu_percent > 50 ? "warn" : "ok";

  return (
    <div className="min-h-screen bg-[#0a0a0e] p-4 sm:p-6 text-white" data-testid="runtime-status-page">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black" data-testid="runtime-status-title">
            🩺 Runtime Status
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            Production stability dashboard · polls every 10 s
          </p>
        </div>
        <div className="flex items-center gap-2">
          {lastFetch && (
            <span className="text-[11px] text-gray-500">
              Updated {lastFetch.toLocaleTimeString()}
            </span>
          )}
          <Button
            onClick={load}
            disabled={loading}
            size="sm"
            variant="outline"
            className="border-white/20 text-white hover:bg-white/10"
            data-testid="runtime-status-refresh"
          >
            {loading ? "…" : "Refresh"}
          </Button>
        </div>
      </div>

      {restartBanner && (
        <Card
          className="p-4 mb-5 border-amber-400/40 bg-amber-500/5"
          data-testid="restart-banner"
        >
          <div className="text-amber-200 font-black flex items-center gap-2">
            ⚠️ Backend restart detected
          </div>
          <div className="text-amber-100/80 text-sm mt-1">
            Previous start: <code>{restartBanner.old}</code><br />
            New start:&nbsp;&nbsp;&nbsp;&nbsp;&nbsp; <code>{restartBanner.new}</code>
          </div>
          <button
            onClick={() => setRestartBanner(null)}
            className="text-xs text-amber-300 mt-2 underline"
            data-testid="restart-banner-dismiss"
          >
            Dismiss
          </button>
        </Card>
      )}

      {/* Phase 15.6D — Cloudinary (Media) health card */}
      {cloudHealth && (
        <Card
          className={`p-4 mb-5 border ${cloudHealth.cloudinary?.ok ? "border-emerald-400/40 bg-emerald-500/5" : "border-rose-500/50 bg-rose-500/10"}`}
          data-testid="cloudinary-health-card"
        >
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className={`font-black text-base flex items-center gap-2 ${cloudHealth.cloudinary?.ok ? "text-emerald-200" : "text-rose-100"}`}>
                {cloudHealth.cloudinary?.ok ? "✅ Media service (Cloudinary) — healthy" : "🚨 Media service (Cloudinary) — DOWN"}
                <span className="text-xs font-normal text-gray-400">cloud: {cloudHealth.cloudinary?.cloud_name || "?"}</span>
              </div>
              {!cloudHealth.cloudinary?.ok && (
                <div className="text-rose-100/80 text-sm mt-1" data-testid="cloudinary-down-detail">
                  reason: <span className="font-bold text-rose-200">{cloudHealth.cloudinary?.reason || "unknown"}</span>
                  {cloudHealth.cloudinary?.http_status && (
                    <> · HTTP {cloudHealth.cloudinary.http_status}</>
                  )}
                  {cloudHealth.cloudinary?.raw_body_preview && (
                    <div className="mt-1 px-2 py-1 rounded bg-black/30 font-mono text-[11px] break-all">
                      {cloudHealth.cloudinary.raw_body_preview}
                    </div>
                  )}
                  {cloudHealth.cloudinary?.reason === "cloud_name_disabled" && (
                    <div className="mt-2 text-amber-200">
                      <strong>Action required:</strong> Cloudinary account is suspended. Log into{" "}
                      <a className="underline" href="https://cloudinary.com/console" target="_blank" rel="noopener noreferrer">cloudinary.com/console</a>,
                      check billing / usage caps, and re-enable the account.
                      Uploads + playback are broken until this is fixed.
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="text-right text-xs text-gray-300">
              <div data-testid="cloudinary-latency">latency: {cloudHealth.cloudinary?.latency_ms ?? "—"} ms</div>
            </div>
          </div>
          {/* DB integrity counters */}
          {cloudHealth.db_integrity && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 text-xs">
              <div className="rounded bg-white/[0.04] px-2 py-1.5">
                <div className="text-gray-400">Total tracks</div>
                <div className="text-white font-bold">{cloudHealth.db_integrity.total_submissions}</div>
              </div>
              <div className="rounded bg-white/[0.04] px-2 py-1.5">
                <div className="text-gray-400">With music_url</div>
                <div className="text-emerald-300 font-bold">{cloudHealth.db_integrity.with_music_url}</div>
              </div>
              <div className={`rounded px-2 py-1.5 ${cloudHealth.db_integrity.music_file_only_no_url > 0 ? "bg-amber-500/10" : "bg-white/[0.04]"}`}>
                <div className="text-gray-400">Missing music_url</div>
                <div className={`font-bold ${cloudHealth.db_integrity.music_file_only_no_url > 0 ? "text-amber-300" : "text-white"}`}>
                  {cloudHealth.db_integrity.music_file_only_no_url}
                </div>
              </div>
              <div className={`rounded px-2 py-1.5 ${cloudHealth.db_integrity.featured_missing_url > 0 ? "bg-rose-500/10" : "bg-white/[0.04]"}`}>
                <div className="text-gray-400">Featured missing URL</div>
                <div className={`font-bold ${cloudHealth.db_integrity.featured_missing_url > 0 ? "text-rose-300" : "text-white"}`}>
                  {cloudHealth.db_integrity.featured_missing_url}
                </div>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Top row — at-a-glance health */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <StatCard
          label="Uptime"
          value={data?.uptime?.uptime_human || "—"}
          tone={data?.uptime?.uptime_seconds > 600 ? "ok" : "warn"}
          hint={data?.uptime?.uptime_seconds < 600 ? "🟡 Recent restart" : null}
        />
        <StatCard
          label="DB ping"
          value={dbOk ? `${dbLat} ms` : "FAIL"}
          tone={dbTone}
          hint={dbOk ? null : data?.db?.error}
        />
        <StatCard
          label="Memory (RSS)"
          value={sys.memory_rss_mb != null ? `${sys.memory_rss_mb} MB` : "—"}
          tone={memTone}
          hint={sys.system_memory_used_pct != null ? `host ${sys.system_memory_used_pct}%` : null}
        />
        <StatCard
          label="CPU"
          value={sys.cpu_percent != null ? `${sys.cpu_percent}%` : "—"}
          tone={cpuTone}
          hint={sys.num_threads ? `${sys.num_threads} threads` : null}
        />
      </div>

      {/* Background tasks */}
      <Card className="p-4 mb-5 bg-white/[0.02] border-white/10" data-testid="bg-tasks-card">
        <h2 className="text-base font-black mb-3 flex items-center gap-2">
          ⚙️ Background tasks
          <Pill tone={
            (data?.background_tasks || []).some((t) => t.stalled) ? "bad" : "ok"
          }>
            {(data?.background_tasks || []).filter((t) => !t.stalled).length}/
            {(data?.background_tasks || []).length} healthy
          </Pill>
        </h2>
        <div className="space-y-2">
          {(data?.background_tasks || []).map((t) => (
            <div
              key={t.name}
              data-testid={`bg-task-${t.name}`}
              className={`flex items-center justify-between gap-3 rounded-lg px-3 py-2 border ${
                t.stalled ? "border-rose-400/40 bg-rose-500/5"
                : !t.ok    ? "border-amber-400/40 bg-amber-500/5"
                : "border-emerald-400/20 bg-emerald-500/[0.03]"
              }`}
            >
              <div className="min-w-0">
                <div className="text-white font-bold text-sm flex items-center gap-2">
                  {t.name}
                  {t.stalled
                    ? <Pill tone="bad">STALLED</Pill>
                    : !t.ok
                    ? <Pill tone="warn">LAST TICK FAILED</Pill>
                    : <Pill tone="ok">OK</Pill>}
                </div>
                <div className="text-[11px] text-gray-400 mt-0.5">
                  Period {t.period_seconds}s · last tick {fmtAge(t.age_seconds)} ago
                  {t.detail && t.detail !== "starting" ? ` · ${t.detail}` : ""}
                </div>
              </div>
            </div>
          ))}
          {(data?.background_tasks || []).length === 0 && (
            <div className="text-gray-400 text-sm">No background tasks registered.</div>
          )}
        </div>
      </Card>

      {/* Recent errors */}
      <Card className="p-4 bg-white/[0.02] border-white/10" data-testid="recent-errors-card">
        <h2 className="text-base font-black mb-3 flex items-center gap-2">
          📝 Recent unhandled errors
          <Pill tone={
            (data?.recent_errors || []).length === 0 ? "ok"
            : (data?.recent_errors || []).length > 10 ? "bad" : "warn"
          }>
            {(data?.recent_errors || []).length} captured
          </Pill>
        </h2>
        {(data?.recent_errors || []).length === 0 ? (
          <div className="text-emerald-300/80 text-sm" data-testid="no-recent-errors">
            🟢 No errors recorded since last process start.
          </div>
        ) : (
          <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
            {(data?.recent_errors || []).map((e, i) => (
              <div
                key={i}
                data-testid={`recent-error-${i}`}
                className="rounded-md border border-rose-400/20 bg-rose-500/5 px-3 py-2"
              >
                <div className="flex items-center gap-2 flex-wrap">
                  <Pill tone="bad">{e.type}</Pill>
                  <code className="text-[11px] text-rose-200">{e.where}</code>
                  <span className="text-[10px] text-gray-400 ml-auto">{e.at}</span>
                </div>
                <div className="text-sm text-rose-100 mt-1 font-mono break-words">{e.message}</div>
                {e.request_path && (
                  <div className="text-[10px] text-gray-400 mt-1">@ {e.request_path}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      <p className="mt-6 text-[11px] text-gray-500" data-testid="runtime-status-footer">
        Endpoints: <code>/api/health</code> (public, fast) ·{" "}
        <code>/api/health/deep</code> (public, includes DB ping) ·{" "}
        <code>/api/admin/status/diagnostics</code> (admin, this page).
      </p>
    </div>
  );
};

export default AdminRuntimeStatusPage;
